<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18"></h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/member-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active"></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-primary">
                        <i class="bx bx-wallet text-primary"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Annual Premium</p>
                      <h4 class="mb-0">ETB {{ formatCurrency(profile.annual_premium) }}</h4>
                      <small class="text-muted">{{ currentYear }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-success">
                        <i class="bx bx-check-circle text-success"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Total Paid</p>
                      <h4 class="mb-0">ETB {{ formatCurrency(totalPaidThisYear) }}</h4>
                      <small class="text-muted">This year</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon" :class="remainingBalance > 0 ? 'bg-soft-warning' : 'bg-soft-success'">
                        <i class="bx" :class="remainingBalance > 0 ? 'bx-time text-warning' : 'bx-badge-check text-success'"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Balance Due</p>
                      <h4 class="mb-0" :class="remainingBalance > 0 ? 'text-warning' : 'text-success'">
                        {{ remainingBalance > 0 ? 'ETB ' + formatCurrency(remainingBalance) : 'Paid' }}
                      </h4>
                      <small class="text-muted">{{ remainingBalance > 0 ? 'Outstanding' : 'Fully paid' }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card stat-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="stat-icon bg-soft-info">
                        <i class="bx bx-receipt text-info"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted mb-1 fw-medium">Total Payments</p>
                      <h4 class="mb-0">{{ payments.length }}</h4>
                      <small class="text-muted">All time</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filter Bar -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body py-3">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="d-flex flex-wrap align-items-center gap-2">
                        <div class="search-box">
                          <div class="position-relative">
                            <input type="text" class="form-control" placeholder="Search by reference..." v-model="searchQuery">
                            <i class="bx bx-search-alt search-icon"></i>
                          </div>
                        </div>
                        <select class="form-select" style="width: 120px;" v-model="filterYear">
                          <option value="">All Years</option>
                          <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                        </select>
                        <select class="form-select" style="width: 140px;" v-model="filterStatus">
                          <option value="">All Status</option>
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-4 text-md-end mt-3 mt-md-0">
                      <span class="text-muted small">
                        Showing {{ filteredPayments.length }} of {{ payments.length }} payments
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading payment history...</p>
          </div>

          <!-- Payments Content -->
          <div v-else>
            <!-- Payments Table -->
            <div class="card" v-if="filteredPayments.length > 0">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Receipt #</th>
                      <th>Year</th>
                      <th>Method</th>
                      <th class="text-end">Amount</th>
                      <th class="text-center">Status</th>
                      <th class="text-center">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="payment in paginatedPayments" :key="payment.payment_id" class="payment-row">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="date-icon me-2">
                            <span class="day">{{ getDayFromDate(payment.payment_date) }}</span>
                            <span class="month">{{ getMonthFromDate(payment.payment_date) }}</span>
                          </div>
                          <div>
                            <span class="d-block">{{ formatDate(payment.payment_date) }}</span>
                            <small class="text-muted">{{ formatTime(payment.payment_date) }}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="receipt-number">{{ payment.receipt_number || 'N/A' }}</span>
                      </td>
                      <td>
                        <span class="badge bg-soft-primary text-primary">{{ payment.payment_year }}</span>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <i :class="getPaymentMethodIcon(payment.payment_type)" class="me-2 text-muted"></i>
                          <span>{{ payment.method_name || capitalizeFirst(payment.payment_type) }}</span>
                        </div>
                      </td>
                      <td class="text-end">
                        <strong class="text-success">ETB {{ formatCurrency(payment.amount) }}</strong>
                      </td>
                      <td class="text-center">
                          <span class="badge" :class="getStatusBadgeClass(payment.status)">
                            <i :class="getStatusIcon(payment.status)" class="me-1"></i>
                            {{ capitalizeFirst(payment.status) }}
                          </span>
                      </td>
                      <td class="text-center">
                        <button class="btn btn-soft-primary btn-sm" @click="viewReceipt(payment)" title="View Receipt">
                          <i class="bx bx-receipt"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="card-footer bg-white border-top" v-if="totalPages > 1">
                  <div class="d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredPayments.length }} entries
                    </small>
                    <nav>
                      <ul class="pagination pagination-sm mb-0">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                          <a class="page-link" href="#" @click.prevent="currentPage--">
                            <i class="bx bx-chevron-left"></i>
                          </a>
                        </li>
                        <li class="page-item" v-for="page in displayedPages" :key="page"
                            :class="{ active: currentPage === page }">
                          <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                          <a class="page-link" href="#" @click.prevent="currentPage++">
                            <i class="bx bx-chevron-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Cards for Mobile -->
            <div class="d-md-none" v-if="filteredPayments.length > 0">
              <div class="card payment-card mb-2" v-for="payment in paginatedPayments" :key="'mobile-' + payment.payment_id">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 class="mb-1">{{ formatDate(payment.payment_date) }}</h6>
                      <small class="text-muted">{{ payment.receipt_number }}</small>
                    </div>
                    <span class="badge" :class="getStatusBadgeClass(payment.status)">
                      {{ capitalizeFirst(payment.status) }}
                    </span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <span class="text-muted small">{{ payment.method_name }}</span>
                      <span class="badge bg-soft-primary text-primary ms-2">{{ payment.payment_year }}</span>
                    </div>
                    <div class="text-end">
                      <strong class="text-success">ETB {{ formatCurrency(payment.amount) }}</strong>
                      <button class="btn btn-soft-primary btn-sm ms-2" @click="viewReceipt(payment)">
                        <i class="bx bx-receipt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Payments -->
            <div class="card" v-if="payments.length === 0">
              <div class="card-body text-center py-5">
                <i class="bx bx-receipt text-muted" style="font-size: 4rem; opacity: 0.5;"></i>
                <h5 class="text-muted mt-3">No Payment History</h5>
                <p class="text-muted mb-0">You haven't made any payments yet.</p>
              </div>
            </div>

            <!-- No Results from Filter -->
            <div class="card" v-else-if="filteredPayments.length === 0">
              <div class="card-body text-center py-4">
                <i class="bx bx-search-alt text-muted" style="font-size: 3rem;"></i>
                <h5 class="text-muted mt-3">No Results Found</h5>
                <p class="text-muted">No payments match your search criteria.</p>
                <button class="btn btn-outline-primary btn-sm" @click="clearFilters">
                  <i class="bx bx-refresh me-1"></i>Clear Filters
                </button>
              </div>
            </div>

            <!-- Payment Summary by Year -->
            <div class="row mt-4" v-if="payments.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-bar-chart-alt-2 text-primary me-2"></i>Payment Summary by Year
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Year</th>
                          <th class="text-center">Payments</th>
                          <th class="text-end">Total Paid</th>
                          <th class="text-end">Premium</th>
                          <th class="text-center">Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="summary in yearSummaries" :key="summary.year">
                          <td><strong>{{ summary.year }}</strong></td>
                          <td class="text-center">{{ summary.count }}</td>
                          <td class="text-end text-success">ETB {{ formatCurrency(summary.total) }}</td>
                          <td class="text-end">ETB {{ formatCurrency(profile.annual_premium) }}</td>
                          <td class="text-center">
                              <span class="badge" :class="summary.total >= profile.annual_premium ? 'bg-success' : 'bg-warning'">
                                {{ summary.total >= profile.annual_premium ? 'Paid' : 'Partial' }}
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- Receipt Modal -->
  <div class="modal-overlay" v-if="showReceiptModal" @click.self="closeReceiptModal">
    <div class="modal-container modal-lg">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header receipt-modal-header">
          <h5 class="modal-title text-white">
            <i class="bx bx-receipt me-2"></i>Payment Receipt
          </h5>
          <button type="button" class="btn-close-white" @click="closeReceiptModal">&times;</button>
        </div>

        <!-- Loading state -->
        <div v-if="receiptLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Loading receipt...</p>
        </div>

        <!-- Receipt content -->
        <div v-else-if="receiptData" class="receipt-container" id="receiptContent">
          <!-- Receipt Header -->
          <div class="receipt-header">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div class="logo me-3">CBHI</div>
                <div>
                  <h6 class="mb-0 text-white">Community Based Health Insurance</h6>
                  <p class="mb-0 small text-white-75">Ministry of Health - Ethiopia</p>
                </div>
              </div>
              <div class="text-end">
                <div class="receipt-number">{{ receiptData.payment.receipt_number }}</div>
                <small class="text-white-75">{{ formatDate(receiptData.payment.payment_date) }}</small>
              </div>
            </div>
          </div>

          <!-- Receipt Body -->
          <div class="receipt-body">
            <!-- Member & Payment Info -->
            <div class="row mb-3">
              <div class="col-md-6">
                <h6 class="section-title">Member Information</h6>
                <table class="table table-sm table-borderless mb-0">
                  <tr>
                    <td class="text-muted" width="40%">CBHI Number:</td>
                    <td><strong>{{ receiptData.member.cbhi_number }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Name:</td>
                    <td><strong>{{ receiptData.member.full_name }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Phone:</td>
                    <td>{{ receiptData.member.phone_number || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Location:</td>
                    <td>{{ receiptData.location?.woreda_name }}, {{ receiptData.location?.zone_name }}</td>
                  </tr>
                </table>
              </div>

              <div class="col-md-6">
                <h6 class="section-title">Payment Information</h6>
                <table class="table table-sm table-borderless mb-0">
                  <tr>
                    <td class="text-muted" width="40%">Method:</td>
                    <td><strong>{{ receiptData.payment.method_name }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Reference:</td>
                    <td>{{ receiptData.payment.payment_reference }}</td>
                  </tr>
                  <tr v-if="receiptData.payment.payment_type === 'MOBILE'">
                    <td class="text-muted">Mobile:</td>
                    <td>{{ receiptData.payment.mobileNumber }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Collected By:</td>
                    <td>{{ receiptData.payment.collected_by || 'System' }}</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Payment Summary Table -->
            <div class="mb-3">
              <h6 class="section-title">Payment Summary</h6>
              <table class="table table-bordered table-sm">
                <thead class="table-light">
                <tr>
                  <th>Description</th>
                  <th class="text-center">Beneficiaries</th>
                  <th class="text-end">Amount (ETB)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>Annual CBHI Premium ({{ receiptData.payment.payment_year }})</td>
                  <td class="text-center">{{ receiptData.totalBeneficiaries || '-' }}</td>
                  <td class="text-end"><strong>{{ formatCurrency(receiptData.expectedAnnualPremium) }}</strong></td>
                </tr>
                <tr class="table-success">
                  <td><strong>Total Paid to Date</strong></td>
                  <td class="text-center">-</td>
                  <td class="text-end"><strong>{{ formatCurrency(receiptData.totalPaidForYear) }}</strong></td>
                </tr>
                <tr class="table-info">
                  <td>This Payment</td>
                  <td class="text-center">-</td>
                  <td class="text-end">{{ formatCurrency(receiptData.payment.amount) }}</td>
                </tr>
                <tr :class="getBalanceRowClass()">
                  <td><strong>{{ getBalanceDescription() }}</strong></td>
                  <td class="text-center">-</td>
                  <td class="text-end"><strong>{{ getBalanceAmountText() }}</strong></td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Status & Plan -->
            <div class="row mb-3">
              <div class="col-md-6">
                <h6 class="section-title">Coverage Status</h6>
                <div class="status-card" :class="getPaymentStatusClass()">
                  <div class="d-flex align-items-center">
                    <i :class="getPaymentStatusIcon()" class="fs-4 me-3"></i>
                    <div>
                      <div class="fw-bold">{{ getPaymentStatusText() }}</div>
                      <small>{{ getCoverageText() }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6" v-if="receiptData.plan">
                <h6 class="section-title">Plan Details</h6>
                <table class="table table-sm table-borderless mb-0">
                  <tr>
                    <td class="text-muted">Plan:</td>
                    <td><strong>{{ receiptData.plan.plan_name }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Coverage Limit:</td>
                    <td>ETB {{ formatCurrency(receiptData.plan.annual_coverage_limit) }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Visit Limit:</td>
                    <td>ETB {{ formatCurrency(receiptData.plan.per_visit_limit) }}</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Notes -->
            <div v-if="receiptData.payment.notes" class="mb-2">
              <h6 class="section-title">Notes</h6>
              <p class="notes-text">{{ receiptData.payment.notes }}</p>
            </div>
          </div>

          <!-- Receipt Footer -->
          <div class="receipt-footer">
            <div class="row">
              <div class="col-md-8">
                <div class="signature-section">
                  <div class="row">
                    <div class="col-6">
                      <div class="signature-line">
                        <strong style="font-size: 10px;">{{ receiptData.payment.collected_by || 'System' }}</strong>
                        <div class="small text-muted">Received By</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="signature-line">
                        <div class="border-bottom mb-1" style="height: 12px;"></div>
                        <div class="small text-muted">Member Signature</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-end">
                <div class="stamp-area">
                  <div class="text-muted small" style="font-size: 9px;">Official Stamp</div>
                  <div class="border rounded p-2 mt-1" style="height: 45px;"></div>
                </div>
              </div>
            </div>

            <div class="print-info mt-2 pt-2 border-top">
              <div class="row text-center small text-muted">
                <div class="col-md-4">Generated: {{ formatDateTime(new Date()) }}</div>
                <div class="col-md-4">Hotline: 8335</div>
                <div class="col-md-4">www.moh.gov.et</div>
              </div>
              <p class="text-center mb-0 mt-1 small text-muted">This is a computer-generated receipt. Please keep it for your records.</p>
            </div>
          </div>
        </div>

        <!-- Error state -->
        <div v-else class="text-center py-5">
          <i class="bx bx-error-circle text-danger" style="font-size: 4rem;"></i>
          <p class="text-muted mt-3">Failed to load receipt data</p>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeReceiptModal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-primary" @click="printReceipt" :disabled="!receiptData">
            <i class="bx bx-printer me-1"></i>Print
          </button>
        </div>
      </div>
    </div>
  </div>

  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import { memberUrl, execute } from "@/api";

export default {
  name: "PaymentHistory",
  components: {
    MemberPortalNavigation
  },

  data() {
    return {
      loading: false,

      // Profile data
      profile: {},
      payments: [],
      totalPaidThisYear: 0,

      // Filters
      searchQuery: '',
      filterYear: '',
      filterStatus: '',

      // Pagination
      currentPage: 1,
      perPage: 10,

      // Receipt Modal
      showReceiptModal: false,
      receiptLoading: false,
      receiptData: null
    };
  },

  computed: {
    currentYear() {
      return new Date().getFullYear();
    },

    remainingBalance() {
      const annual = parseFloat(this.profile.annual_premium) || 0;
      const paid = parseFloat(this.totalPaidThisYear) || 0;
      return Math.max(0, annual - paid);
    },

    availableYears() {
      const years = [...new Set(this.payments.map(p => p.payment_year))];
      return years.sort((a, b) => b - a);
    },

    filteredPayments() {
      let result = [...this.payments];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(p =>
            (p.receipt_number && p.receipt_number.toLowerCase().includes(query)) ||
            (p.payment_reference && p.payment_reference.toLowerCase().includes(query))
        );
      }

      // Year filter
      if (this.filterYear) {
        result = result.filter(p => p.payment_year == this.filterYear);
      }

      // Status filter
      if (this.filterStatus) {
        result = result.filter(p => p.status === this.filterStatus);
      }

      // Sort by date descending
      result.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));

      return result;
    },

    totalPages() {
      return Math.ceil(this.filteredPayments.length / this.perPage);
    },

    paginatedPayments() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredPayments.slice(start, end);
    },

    paginationStart() {
      return (this.currentPage - 1) * this.perPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.filteredPayments.length);
    },

    displayedPages() {
      const pages = [];
      const maxPages = 5;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxPages - 1);

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    yearSummaries() {
      const summaries = {};

      this.payments.forEach(p => {
        if (!summaries[p.payment_year]) {
          summaries[p.payment_year] = { year: p.payment_year, count: 0, total: 0 };
        }
        summaries[p.payment_year].count++;
        summaries[p.payment_year].total += parseFloat(p.amount) || 0;
      });

      return Object.values(summaries).sort((a, b) => b.year - a.year);
    },

    // Receipt computed properties
    balanceAmount() {
      if (!this.receiptData?.payment_summary) return 0;
      return this.receiptData.payment_summary.balance_due || 0;
    },

    isFullyPaid() {
      return Math.abs(this.balanceAmount) < 0.01;
    },

    hasOutstandingBalance() {
      return this.balanceAmount > 0.01;
    },

    isOverpaid() {
      return this.balanceAmount < -0.01;
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    filterYear() {
      this.currentPage = 1;
    },
    filterStatus() {
      this.currentPage = 1;
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        await Promise.all([
          this.loadMemberProfile(),
          this.loadPaymentHistory()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadMemberProfile() {
      const data = {
        function: "getMyMemberProfile"
      };

      try {
        const res = await execute(data, memberUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile || {};
          this.totalPaidThisYear = res.data.data.totalPaidThisYear || 0;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    },

    async loadPaymentHistory() {
      const data = {
        function: "getMyPaymentHistory"
      };

      try {
        const res = await execute(data, memberUrl);

        if (res.data.success) {
          this.payments = res.data.data.payments || [];
        }
      } catch (error) {
        console.error('Error loading payments:', error);
      }
    },

    async viewReceipt(payment) {
      this.showReceiptModal = true;
      this.receiptLoading = true;
      this.receiptData = null;

      try {
        const data = {
          function: "getMyPaymentDetails",
          paymentId: payment.payment_id
        };

        const res = await execute(data, memberUrl);

        if (res.data.success) {
          this.receiptData = res.data.data;
        }
      } catch (error) {
        console.error('Error loading receipt:', error);
      } finally {
        this.receiptLoading = false;
      }
    },

    closeReceiptModal() {
      this.showReceiptModal = false;
      this.receiptData = null;
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterYear = '';
      this.filterStatus = '';
    },

    // Receipt methods
    getBalanceDescription() {
      if (this.isFullyPaid) return 'Payment Status';
      if (this.hasOutstandingBalance) return 'Outstanding Balance';
      if (this.isOverpaid) return 'Overpayment';
      return 'Balance';
    },

    getBalanceAmountText() {
      if (this.isFullyPaid) return 'PAID IN FULL';
      if (this.hasOutstandingBalance) return `ETB ${this.formatCurrency(this.balanceAmount)}`;
      if (this.isOverpaid) return `ETB ${this.formatCurrency(Math.abs(this.balanceAmount))} Credit`;
      return 'ETB 0';
    },

    getBalanceRowClass() {
      if (this.isFullyPaid) return 'table-success';
      if (this.hasOutstandingBalance) return 'table-warning';
      if (this.isOverpaid) return 'table-info';
      return '';
    },

    getPaymentStatusText() {
      if (this.isFullyPaid) return 'PAID IN FULL';
      if (this.hasOutstandingBalance) return 'PARTIAL PAYMENT';
      if (this.isOverpaid) return 'OVERPAID';
      return 'PAYMENT COMPLETE';
    },

    getPaymentStatusClass() {
      if (this.isFullyPaid) return 'status-paid';
      if (this.hasOutstandingBalance) return 'status-partial';
      if (this.isOverpaid) return 'status-overpaid';
      return 'status-paid';
    },

    getPaymentStatusIcon() {
      if (this.isFullyPaid) return 'bx bx-check-circle';
      if (this.hasOutstandingBalance) return 'bx bx-exclamation-triangle';
      if (this.isOverpaid) return 'bx bx-info-circle';
      return 'bx bx-check-circle';
    },

    getCoverageText() {
      if (!this.receiptData) return '';
      const year = this.receiptData.payment.payment_year;

      if (this.isFullyPaid) {
        return `Coverage: Jan 1, ${year} to Dec 31, ${year}`;
      } else {
        return `Coverage pending: ETB ${this.formatCurrency(this.balanceAmount)} remaining`;
      }
    },

    printReceipt() {
      const printWindow = window.open('', '_blank');
      const receiptContent = document.getElementById('receiptContent').outerHTML;

      const printDocument = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>CBHI Payment Receipt</title>
          <style>
            ${this.getPrintStyles()}
          </style>
        </head>
        <body>
          ${receiptContent}
        </body>
        </html>
      `;

      printWindow.document.write(printDocument);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    },

    getPrintStyles() {
      return `
        @page {
          size: A4;
          margin: 10mm;
        }

        body {
          font-family: Arial, sans-serif;
          font-size: 11px;
          line-height: 1.3;
          color: #333;
          margin: 0;
          padding: 0;
        }

        .receipt-container {
          max-width: 210mm;
          margin: 0 auto;
          background: white;
        }

        .receipt-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
          padding: 15px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .logo {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #1e3a8a;
          font-weight: bold;
          font-size: 14px;
        }

        .receipt-number {
          background: rgba(255,255,255,0.2);
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 10px;
        }

        .receipt-body {
          padding: 15px;
        }

        .section-title {
          font-size: 12px;
          font-weight: bold;
          color: #1e3a8a;
          margin-bottom: 6px;
          border-bottom: 2px solid #3b82f6;
          padding-bottom: 3px;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
        }

        .table th, .table td {
          padding: 4px 6px;
          text-align: left;
        }

        .table-bordered th, .table-bordered td {
          border: 1px solid #dee2e6;
        }

        .status-card {
          padding: 10px;
          border-radius: 5px;
          border: 2px solid;
        }

        .status-paid {
          background: #d1e7dd;
          border-color: #22c55e;
          color: #166534;
        }

        .status-partial {
          background: #fff3cd;
          border-color: #f59e0b;
          color: #92400e;
        }

        .receipt-footer {
          background: #f9fafb;
          padding: 15px;
          border-top: 1px solid #e5e7eb;
        }

        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: bold; }
        .text-muted { color: #6c757d; }
        .text-white { color: white; }
        .text-white-75 { color: rgba(255,255,255,0.75); }

        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }

        .row { display: flex; flex-wrap: wrap; margin: 0 -8px; }
        .col-md-4, .col-md-6, .col-md-8, .col-6 { padding: 0 8px; }
        .col-md-4 { width: 33.33%; }
        .col-md-6 { width: 50%; }
        .col-md-8 { width: 66.67%; }
        .col-6 { width: 50%; }

        .mb-0 { margin-bottom: 0; }
        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 12px; }
        .me-2 { margin-right: 8px; }
        .me-3 { margin-right: 12px; }
        .mt-1 { margin-top: 4px; }
        .mt-2 { margin-top: 8px; }
        .pt-2 { padding-top: 8px; }
        .p-2 { padding: 8px; }

        .border { border: 1px solid #dee2e6; }
        .border-bottom { border-bottom: 1px solid #dee2e6; }
        .border-top { border-top: 1px solid #dee2e6; }
        .rounded { border-radius: 4px; }

        .small { font-size: 9px; }
        h6 { font-size: 11px; margin: 0 0 6px 0; }

        .table-success { background-color: #d1e7dd !important; }
        .table-info { background-color: #cff4fc !important; }
        .table-warning { background-color: #fff3cd !important; }
        .table-light { background-color: #f8f9fa !important; }
      `;
    },

    // Utility methods
    getPaymentMethodIcon(type) {
      const icons = {
        'CASH': 'bx bx-money',
        'MOBILE': 'bx bx-mobile',
        'BANK': 'bx bx-building',
        'CARD': 'bx bx-credit-card',
        'TRANSFER': 'bx bx-transfer'
      };
      return icons[type?.toUpperCase()] || 'bx bx-wallet';
    },

    getStatusBadgeClass(status) {
      const classes = {
        'completed': 'bg-success',
        'pending': 'bg-warning text-dark',
        'failed': 'bg-danger',
        'cancelled': 'bg-secondary'
      };
      return classes[status?.toLowerCase()] || 'bg-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'completed': 'bx bx-check',
        'pending': 'bx bx-time',
        'failed': 'bx bx-x',
        'cancelled': 'bx bx-block'
      };
      return icons[status?.toLowerCase()] || 'bx bx-help-circle';
    },

    getDayFromDate(date) {
      if (!date) return '--';
      return new Date(date).getDate();
    },

    getMonthFromDate(date) {
      if (!date) return '---';
      return new Date(date).toLocaleDateString('en-US', { month: 'short' });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
    }
  }
};
</script>

<style scoped>
/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 70px 0 60px 0;
  min-height: 100vh;
  background-color: #f8f9fa;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }
}

/* Stat Cards */
.stat-card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Soft Backgrounds */
.bg-soft-primary { background-color: rgba(44, 90, 160, 0.15); }
.bg-soft-success { background-color: rgba(40, 167, 69, 0.15); }
.bg-soft-info { background-color: rgba(23, 162, 184, 0.15); }
.bg-soft-warning { background-color: rgba(255, 193, 7, 0.15); }

/* Search Box */
.search-box {
  position: relative;
  min-width: 200px;
}

.search-box .form-control {
  padding-left: 40px;
  border-radius: 0.5rem;
}

.search-box .search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

/* Cards */
.card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

/* Table */
.table {
  margin-bottom: 0;
}

.table th {
  font-weight: 600;
  font-size: 0.85rem;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.table td {
  vertical-align: middle;
  font-size: 0.9rem;
}

.payment-row:hover {
  background-color: #f8f9fa;
}

/* Date Icon */
.date-icon {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.date-icon .day {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
}

.date-icon .month {
  font-size: 0.65rem;
  text-transform: uppercase;
}

/* Receipt Number */
.receipt-number {
  font-family: monospace;
  font-size: 0.85rem;
  color: #2c5aa0;
}

/* Soft Buttons */
.btn-soft-primary {
  background-color: rgba(44, 90, 160, 0.1);
  color: #2c5aa0;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #2c5aa0;
  color: #fff;
}

/* Mobile Payment Cards */
.payment-card {
  border-left: 4px solid #2c5aa0;
}

/* Pagination */
.pagination .page-link {
  color: #2c5aa0;
  border-color: #e9ecef;
}

.pagination .page-item.active .page-link {
  background-color: #2c5aa0;
  border-color: #2c5aa0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-container.modal-lg {
  max-width: 900px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e9ecef;
}

.receipt-modal-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 0.75rem 0.75rem 0 0;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e9ecef;
}

.btn-close-white {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;
}

.btn-close-white:hover {
  opacity: 1;
}

/* Receipt Styles */
.receipt-container {
  background: white;
  color: #333;
}

.receipt-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 15px;
}

.logo {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1e3a8a;
  font-weight: bold;
  font-size: 14px;
}

.receipt-header .receipt-number {
  background: rgba(255,255,255,0.2);
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 11px;
  color: white;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75);
}

.receipt-body {
  padding: 20px;
}

.section-title {
  font-size: 13px;
  font-weight: bold;
  color: #1e3a8a;
  margin-bottom: 10px;
  border-bottom: 2px solid #3b82f6;
  padding-bottom: 5px;
}

.table-sm th,
.table-sm td {
  padding: 0.3rem 0.5rem;
  font-size: 0.85rem;
}

.table-borderless td {
  border: 0;
  padding: 0.25rem 0;
}

.status-card {
  padding: 12px;
  border-radius: 8px;
  border: 2px solid;
}

.status-paid {
  background: #d1e7dd;
  border-color: #22c55e;
  color: #166534;
}

.status-partial {
  background: #fff3cd;
  border-color: #f59e0b;
  color: #92400e;
}

.status-overpaid {
  background: #dbeafe;
  border-color: #3b82f6;
  color: #1e40af;
}

.receipt-footer {
  background: #f9fafb;
  padding: 15px 20px;
  border-top: 1px solid #e5e7eb;
}

.signature-line {
  margin-bottom: 8px;
}

.stamp-area {
  text-align: center;
}

.print-info {
  font-size: 10px;
  color: #6b7280;
}

.notes-text {
  background: #f9fafb;
  padding: 10px;
  border-radius: 5px;
  font-style: italic;
  color: #4b5563;
  font-size: 12px;
}

/* Hide desktop table on mobile */
@media (max-width: 767px) {
  .table-responsive {
    display: none;
  }
}

/* Hide mobile cards on desktop */
@media (min-width: 768px) {
  .d-md-none {
    display: none !important;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    margin: 0.5rem;
    max-height: 95vh;
  }

  .receipt-body {
    padding: 15px;
  }

  .receipt-header {
    padding: 12px;
  }

  .section-title {
    font-size: 12px;
  }

  .row > [class*="col-md-"] {
    width: 100%;
    margin-bottom: 15px;
  }
}
</style>